import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import { Public as PublicIcon } from '@mui/icons-material';

// Planet type icons (reuse from PlanetDetails)
const planetTypeIcons = {
  terrestrial: <PublicIcon />,
  artificial_habitat: <PublicIcon />,
  rogue_planet: <PublicIcon />,
  gas_giant: <PublicIcon />,
  earth_like: <PublicIcon />,
  crystal_forests: <PublicIcon />,
  orbital_fortress: <PublicIcon />,
  dark_terrestrial: <PublicIcon />
};

function GalaxyDetails({ galaxy, planets, races, factions }) {
  if (!galaxy) return <Alert severity="info">Select a galaxy to view details</Alert>;

  const galaxyPlanets = planets.filter(planet => planet.galaxy_id === galaxy.id);

  return (
    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
      {/* Basic Information */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{galaxy.name}</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {galaxy.type} • Diameter: {galaxy.diameter_ly} light years
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Stars:</strong> {galaxy.stars_count_estimate}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Core:</strong> {galaxy.core_status}</Typography>
            </Grid>
            {galaxy.environment_profile?.zero_jump_stability && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Zero Jump Stability:</strong> {galaxy.environment_profile.zero_jump_stability}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Environment Profile */}
      {galaxy.environment_profile && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>🌌 Environment Profile</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Radiation Level:</strong> {galaxy.environment_profile.radiation_level}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Anomaly Density:</strong> {galaxy.environment_profile.anomaly_density}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Civilizations */}
      {galaxy.civilizations && galaxy.civilizations.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>🏛️ Civilizations</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {galaxy.civilizations.map((civ, index) => (
                <Chip key={index} label={civ} size="small" variant="outlined" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Known Sectors */}
      {galaxy.known_sectors && galaxy.known_sectors.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>🗺️ Known Sectors</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {galaxy.known_sectors.map((sector, index) => (
                <Chip key={index} label={sector} size="small" color="primary" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Planets in Galaxy */}
      {galaxyPlanets.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>🪐 Planets ({galaxyPlanets.length})</Typography>
            <List dense>
              {galaxyPlanets.map(planet => (
                <ListItem key={planet.id} divider>
                  <ListItemIcon>
                    {planetTypeIcons[planet.subtype || planet.type] || <PublicIcon />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={planet.name} 
                    secondary={
                      <Box>
                        <Typography variant="body2">{planet.system_id}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={planet.type} size="small" variant="outlined" />
                          <Chip label={planet.environment} size="small" />
                          {planet.atmosphere_type && (
                            <Chip label={planet.atmosphere_type} size="small" variant="outlined" />
                          )}
                        </Box>
                      </Box>
                    } 
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default GalaxyDetails;